/**
 * Wave 4 SEO cluster (part B): master’s CV, speculative application,
 * US vs EU CV, PDF file size, remote work. Spanish and English.
 * Merged from posts.js. Mid CTA variant `mid`.
 */

export default [
  {
    slug: 'cv-para-master',
    date: '2026-08-22',
    category: 'career',
    related: ['tfg-tfm-en-el-cv', 'idiomas-en-el-cv', 'cv-ingles-o-espanol'],
    es: {
      title: 'CV para un máster: qué cambia respecto al laboral',
      description:
        'Cómo adaptar el currículum a una admisión de máster o posgrado: expediente, TFG, idiomas. No es el mismo PDF que InfoJobs. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Un CV para un máster no pelea el mismo filtro que un CV para un empleo. Admisión mira expediente, TFG, idiomas, cartas y por qué ese programa. RR. HH. mira logros y keywords de la vacante. Reciclar el PDF de InfoJobs suele dejar fuera la nota media y meter un resumen “busco retos”. Reciclar el CV académico de cuatro páginas en un portal de empleo es el error inverso. Si ya tienes un archivo, recorta y reordena; no hace falta una biografía nueva.',
        },
        {
          type: 'p',
          text: 'Hermanos: [TFG y TFM](/blog/tfg-tfm-en-el-cv), [idiomas MCER](/blog/idiomas-en-el-cv), [inglés o español](/blog/cv-ingles-o-espanol). ClarCV genera A4 parseable; no sustituye el formulario de la universidad ni el Europass si el pliego lo pide. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        {
          type: 'p',
          text: 'Lee la convocatoria: página límite, foto, idioma, si quieren CV o el formulario propio. Eso manda más que cualquier plantilla “moderna”.',
        },
        { type: 'h2', text: 'Qué subir y qué bajar de prioridad' },
        {
          type: 'p',
          text: 'Antes (PDF laboral enviado a un máster de datos): “CSM con foco en churn”, sin nota, TFG en una línea vaga. Después: formación primero (grado, nota media si es competitiva, TFG con método), idiomas con certificado, 2–3 experiencias o prácticas relacionadas, un párrafo de motivación de 4 líneas — o la carta aparte si la piden. Publicaciones, congresos y becas suben. Objetivos de “líderar equipos” bajan salvo que el máster sea executive.',
        },
        {
          type: 'ul',
          items: [
            'Formación arriba: título, centro, años, nota si ayuda, menciones.',
            'TFG/TFM como proyecto, no como muro: ver la guía hermana.',
            'Idiomas con MCER y examen (IELTS/TOEFL si el máster es anglosajón).',
            'Prácticas y empleo: las que demuestran el área del programa.',
            'Carta o motivation letter: archivo distinto si la universidad la pide; no la fusiones al CV.',
          ],
        },
        { type: 'h2', text: 'Europass, foto y longitud' },
        {
          type: 'p',
          text: 'Algunos másteres europeos aún pegan Europass. Si lo exigen, no envíes solo el Canva. Si no lo exigen, un CV de 1–2 páginas suele leerse mejor: [Europass o CV moderno](/blog/europass-o-cv-moderno). Foto: sigue la norma del país de la universidad, no la de tu portal de empleo. Longitud: 2 páginas de méritos académicos pesan más que 3 de hostelería no relacionada — recorta con criterio, no con vergüenza.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El PDF de InfoJobs no es un dossier de admisión',
          lead: 'Si el máster necesita nota, TFG e idiomas y tu archivo es un CV de ventas, reordena. Sube el Word o PDF y deja formación en texto claro.',
          from: 'CV laboral genérico',
          to: 'CV de admisión legible',
          button: 'Adaptar mi CV al máster',
        },
        { type: 'h2', text: 'Empresa que paga el máster vs admisión clásica' },
        {
          type: 'p',
          text: 'Si el posgrado lo patrocina tu empresa, el interlocutor puede ser RR. HH. interno: entonces el CV laboral actualizado suma. Si es un comité académico, no asumas que conocen tu jerga de OKRs. Traduce a problema, datos, entregable. En ClarCV cambias plantilla; tú no inventas un 8,7 de expediente. El modelo no debe fabricar una matrícula de honor.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Mando el mismo CV a un máster y a una oferta de empleo?',
              a: 'No. Reordena. Admisión ≠ screening de vacante. Conserva hechos; cambia énfasis.',
            },
            {
              q: '¿Pongo la nota de selectividad?',
              a: 'Solo si el programa la pide o si eres muy junior y es un imán. El grado pesa más.',
            },
            {
              q: '¿CV en inglés para un máster en España?',
              a: 'El idioma de la convocatoria. Muchos programas bilingües piden EN. Detalle: la guía EN/ES.',
            },
            {
              q: '¿ClarCV envía la solicitud a la universidad?',
              a: 'No. Prepara el PDF. El formulario, tasas y cartas son tuyos.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV for a master’s: what changes vs a job application',
      description:
        'How to adapt a résumé for master’s or postgraduate admission: grades, thesis, languages. Not the same PDF as a job board. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A CV for a master’s does not fight the same filter as a CV for a job. Admissions look at grades, thesis, languages, letters, and why that programme. HR looks at achievements and vacancy keywords. Recycling the InfoJobs PDF often drops the GPA and inserts a “seeking challenges” summary. Recycling a four-page academic CV on a job board is the reverse error. If you already have a file, reorder; you do not need a new biography.',
        },
        {
          type: 'p',
          text: 'Siblings: [thesis on the CV](/blog/tfg-tfm-en-el-cv), [CEFR languages](/blog/idiomas-en-el-cv), [English or Spanish](/blog/cv-ingles-o-espanol). ClarCV generates parseable A4; it does not replace the university form or Europass if the spec requires it. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        {
          type: 'p',
          text: 'Read the call: page limit, photo, language, whether they want a CV or their own form. That beats any “modern” template.',
        },
        { type: 'h2', text: 'What to raise and what to drop' },
        {
          type: 'p',
          text: 'Before (job PDF sent to a data master’s): “CSM focused on churn”, no grade, vague thesis line. After: education first (degree, GPA if competitive, thesis with method), languages with certificates, 2–3 related jobs or internships, a 4-line motivation — or a separate letter if they ask. Publications, conferences, and grants go up. “Lead teams” goals go down unless it is an executive master’s.',
        },
        {
          type: 'ul',
          items: [
            'Education at the top: title, school, years, grade if it helps, honours.',
            'Thesis as a project, not a wall: see the sibling guide.',
            'Languages with CEFR and a test (IELTS/TOEFL if the master’s is Anglophone).',
            'Internships and jobs: the ones that show the programme’s field.',
            'Motivation letter: a separate file if the university asks; do not merge it into the CV.',
          ],
        },
        { type: 'h2', text: 'Europass, photo, and length' },
        {
          type: 'p',
          text: 'Some European master’s still glue Europass. If they require it, do not send only Canva. If they do not, a 1–2 page CV usually reads better: [Europass or a modern CV](/blog/europass-o-cv-moderno). Photo: follow the university country’s norm, not your job board. Length: 2 pages of academic merit beat 3 of unrelated hospitality — trim with judgement, not shame.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'A job-board PDF is not an admissions file',
          lead: 'If the master’s needs grades, thesis, and languages and your file is a sales CV, reorder. Upload Word or PDF and leave education as clear text.',
          from: 'Generic job CV',
          to: 'A readable admissions CV',
          button: 'Adapt my CV for the master’s',
        },
        { type: 'h2', text: 'Company-sponsored master’s vs classic admissions' },
        {
          type: 'p',
          text: 'If your employer sponsors the degree, the reader may be internal HR: then the current job CV helps. If it is an academic committee, do not assume they know your OKR jargon. Translate to problem, data, deliverable. In ClarCV you change template; you do not invent an 8.7 GPA. The model must not fabricate honours.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do I send the same CV to a master’s and to a job?',
              a: 'No. Reorder. Admissions ≠ vacancy screening. Keep facts; change emphasis.',
            },
            {
              q: 'Should I include school-leaving exam grades?',
              a: 'Only if the programme asks or you are very junior and it is a magnet. The degree weighs more.',
            },
            {
              q: 'English CV for a master’s in Spain?',
              a: 'The language of the call. Many bilingual programmes want EN. Detail: the EN/ES guide.',
            },
            {
              q: 'Does ClarCV submit to the university?',
              a: 'No. It prepares the PDF. The form, fees, and letters are yours.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'candidatura-espontanea-cv',
    date: '2026-08-22',
    category: 'career',
    related: ['enviar-cv-por-email', 'adaptar-cv-a-una-oferta', 'checklist-cv-antes-de-enviar'],
    es: {
      title: 'Candidatura espontánea: CV, email y asunto que funciona',
      description:
        'Cómo enviar un currículum sin oferta publicada: a quién, qué asunto, qué adjunto. No es spam a info@empresa. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Una candidatura espontánea es un CV más un email a una persona concreta, sin vacante pública. No es un bucle a info@, empleo@ y el formulario de “trabaja con nosotros” el mismo día. En España funciona en pymes, estudios, consultoras pequeñas y equipos que aún no han publicado. En grandes con ATS cerrado, a menudo muere en una bandeja. El archivo sigue siendo el mismo tipo de PDF parseable; cambia el destinatario y el gancho.',
        },
        {
          type: 'p',
          text: 'La mecánica del correo (asunto, cuerpo, PDF): [enviar el CV por email](/blog/enviar-cv-por-email). El contenido alineado a un rol: [adaptar a una oferta](/blog/adaptar-cv-a-una-oferta) — aquí la “oferta” es la que tú infieres. Checklist: [antes de enviar](/blog/checklist-cv-antes-de-enviar). ClarCV deja el adjunto A4. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No enviamos emails en tu nombre.',
        },
        {
          type: 'p',
          text: 'Investiga un nombre. Un “Estimados” genérico a 40 empresas es ruido. Un email a la head de People o al hiring manager del equipo, con un logro, es una candidatura.',
        },
        { type: 'h2', text: 'A quién y con qué asunto' },
        {
          type: 'p',
          text: 'Antes: asunto “CV” a empleo@marca.com, cuerpo de tres líneas vacías, Canva de 8 MB. Después: “Candidatura espontánea — Product designer (portfolio + disponibilidad Q3)” a un correo que encontraste en LinkedIn o en la web de equipo. Primera línea: quién eres y por qué esa empresa esta semana (lanzamiento, oficina nueva, artículo del CEO), no “siempre admiré vuestra cultura”. Adjunto: Nombre_Apellido_Puesto.pdf, no curriculumfinal2.pdf.',
        },
        {
          type: 'ul',
          items: [
            'Una empresa por email. Copia oculta masiva se nota y se descarta.',
            'Indica ciudad o remoto en el cuerpo y en la cabecera del CV.',
            'No pidas “un café de 30 min” en el primer contacto si no hay relación previa; ofrece el PDF y 4 líneas de valor.',
            'Sigue a los 8–10 días una vez, no tres.',
            'Si tienen portal, a veces hay que duplicar allí: el email no sustituye un ATS obligatorio.',
          ],
        },
        { type: 'h2', text: 'Qué cambia en el PDF (poco, pero concreto)' },
        {
          type: 'p',
          text: 'El resumen apunta al equipo, no a “cualquier reto”. Keywords del sector de esa empresa (no de otra). Foto y DNI: las reglas de siempre — [qué no poner](/blog/que-no-poner-en-el-cv). No firmes el PDF. Si dices “disponibilidad inmediata”, que sea verdad: [disponibilidad](/blog/disponibilidad-inmediata-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El adjunto sigue teniendo que parsearse',
          lead: 'Una candidatura espontánea con columnas e iconos muere igual que en InfoJobs. Sube el PDF y deja un A4 de una columna.',
          from: 'Canva a info@',
          to: 'PDF limpio a un nombre',
          button: 'Preparar el adjunto',
        },
        { type: 'h2', text: 'Cuándo no merece la pena' },
        {
          type: 'p',
          text: 'Administración con bolsa única, Big Tech con requisición obligatoria, empresas que dicen “solo a través del portal”. Ahí la espontánea es un gesto; el canal oficial manda. Tampoco envíes el mismo texto a veinte startups del mismo edificio el mismo martes. En ClarCV iteras el PDF; el targeting es tuyo. El modelo no debe inventar que “conoces al fundador”.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Adjunto también la carta?',
              a: 'El cuerpo del email es la carta. Un segundo PDF solo si lo piden. Dos adjuntos sin pedir restan.',
            },
            {
              q: '¿Puedo usar LinkedIn InMail en vez de email?',
              a: 'Sí si el perfil está abierto. Sigue siendo un mensaje corto + CV o “puedo enviar PDF”. No copies un muro.',
            },
            {
              q: '¿Pongo pretensión salarial?',
              a: 'En espontánea, mejor en la conversación. En el PDF, no. En el email, solo si ellos lo piden por política.',
            },
            {
              q: '¿ClarCV encuentra el email del recruiter?',
              a: 'No. Formatea el CV. El destinatario lo investigas tú.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Speculative application: CV, email, and a subject that works',
      description:
        'How to send a résumé with no posted job: who, which subject, which attachment. Not spam to info@company. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A speculative application is a CV plus an email to a named person, with no public vacancy. It is not a loop to info@, jobs@, and the “work with us” form on the same day. In Spain it works in SMEs, studios, small consultancies, and teams that have not posted yet. In large firms with a closed ATS, it often dies in a mailbox. The file is still the same kind of parseable PDF; the recipient and the hook change.',
        },
        {
          type: 'p',
          text: 'Email mechanics (subject, body, PDF): [emailing your CV](/blog/enviar-cv-por-email). Content aligned to a role: [tailor to a job](/blog/adaptar-cv-a-una-oferta) — here the “job” is the one you infer. Checklist: [before you send](/blog/checklist-cv-antes-de-enviar). ClarCV leaves an A4 attachment. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not send email on your behalf.',
        },
        {
          type: 'p',
          text: 'Research a name. A generic “Dear team” to 40 companies is noise. An email to the People lead or the team hiring manager, with one achievement, is an application.',
        },
        { type: 'h2', text: 'Who and which subject' },
        {
          type: 'p',
          text: 'Before: subject “CV” to jobs@brand.com, three empty body lines, an 8 MB Canva. After: “Speculative application — Product designer (portfolio + Q3 availability)” to an address you found on LinkedIn or the team page. First line: who you are and why that company this week (launch, new office, CEO article), not “I have always admired your culture.” Attachment: Name_Surname_Role.pdf, not curriculumfinal2.pdf.',
        },
        {
          type: 'ul',
          items: [
            'One company per email. Mass BCC is obvious and discarded.',
            'State city or remote in the body and in the CV header.',
            'Do not ask for a “30-minute coffee” on first contact with no prior relationship; offer the PDF and 4 lines of value.',
            'Follow up once after 8–10 days, not three times.',
            'If they have a portal, you sometimes still have to duplicate there: email does not replace a mandatory ATS.',
          ],
        },
        { type: 'h2', text: 'What changes on the PDF (little, but specific)' },
        {
          type: 'p',
          text: 'The summary points at the team, not “any challenge.” Keywords from that company’s sector (not another’s). Photo and ID: the usual rules — [what not to include](/blog/que-no-poner-en-el-cv). Do not sign the PDF. If you say “immediate availability,” make it true: [availability](/blog/disponibilidad-inmediata-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The attachment still has to parse',
          lead: 'A speculative application with columns and icons dies the same way as on InfoJobs. Upload the PDF and leave a one-column A4.',
          from: 'Canva to info@',
          to: 'A clean PDF to a name',
          button: 'Prepare the attachment',
        },
        { type: 'h2', text: 'When it is not worth it' },
        {
          type: 'p',
          text: 'Public bodies with a single pool, Big Tech with a mandatory requisition, companies that say “portal only.” There, speculative is a gesture; the official channel wins. Do not send the same text to twenty startups in the same building on the same Tuesday. In ClarCV you iterate the PDF; targeting is yours. The model must not invent that “you know the founder.”',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I also attach a cover letter?',
              a: 'The email body is the letter. A second PDF only if they ask. Two unsolicited attachments cost you.',
            },
            {
              q: 'Can I use LinkedIn InMail instead of email?',
              a: 'Yes if the profile is open. Still a short message + CV or “I can send a PDF.” Do not paste a wall.',
            },
            {
              q: 'Should I put salary expectation?',
              a: 'On a speculative note, better in conversation. Not on the PDF. In the email, only if they ask as policy.',
            },
            {
              q: 'Does ClarCV find the recruiter’s email?',
              a: 'No. It formats the CV. You research the recipient.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-americano-vs-europeo',
    date: '2026-08-22',
    category: 'language',
    related: ['cv-ingles-o-espanol', 'foto-en-el-cv', 'que-no-poner-en-el-cv'],
    es: {
      title: 'CV americano vs europeo: resume, foto y A4',
      description:
        'Resume de una página, sin foto ni edad, Letter; CV europeo A4, a veces foto. Cómo adaptar el archivo que ya tienes. No es solo el idioma. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'CV americano vs europeo no es un sinónimo de inglés vs español. El idioma del documento lo decide la oferta: [CV en inglés o en español](/blog/cv-ingles-o-espanol). Aquí cambia el continente del archivo: un résumé US de una página, Letter, sin foto, sin edad, sin estado civil; un CV europeo A4 de una o dos páginas, a veces con foto, idiomas en MCER. Enviar un resume de Nueva York a InfoJobs (o un CV con foto y DNI a Greenhouse en Austin) es el error de formato, no de traducción.',
        },
        {
          type: 'p',
          text: 'Foto: [foto en el CV](/blog/foto-en-el-cv). Campos que sobran: [qué no poner](/blog/que-no-poner-en-el-cv). ClarCV exporta A4 para ES/LatAm/UE. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No generamos formato Letter US como producto principal; si el proceso es 100 % americano, recorta foto y datos y respeta su página.',
        },
        {
          type: 'p',
          text: '“CV” en UK académico puede ser de 3–10 páginas con publicaciones. Eso no es un résumé de producto en San Francisco. Nombra el documento como ellos.',
        },
        { type: 'h2', text: 'Estados Unidos y Canadá (privado)' },
        {
          type: 'p',
          text: 'Resume: 1 página junior/mid; 2 solo con trayectoria densa. Sin foto, sin fecha de nacimiento, sin DNI, sin género, sin nacionalidad salvo work authorization (“US work authorized” / “TN eligible”). Personal statement tipo europeo, fuera. Summary corto. Logros con cifras. Carta (cover letter) aparte si la piden. Tamaño Letter (8,5 × 11 in). El ATS US odia lo mismo: columnas, iconos, tablas.',
        },
        {
          type: 'ul',
          items: [
            'UK privado (no académico): cada vez más “CV” de 2 páginas, a menudo sin foto.',
            'España / buena parte de la UE: A4, 1–2 páginas, foto aún habitual en local, no en filiales US.',
            'Alemania: a veces Lebenslauf más rígido y foto; lee el anuncio.',
            'LatAm: hoja de vida / CV — mapa en [hoja de vida o currículum](/blog/hoja-de-vida-o-curriculum).',
          ],
        },
        { type: 'h2', text: 'Qué tocar en el PDF que ya usas en España' },
        {
          type: 'p',
          text: 'Antes (Madrid → proceso US remoto): foto, “32 años”, dirección con piso, hobbies, dos columnas Canva. Después: nombre, email, teléfono +1 o +34, LinkedIn, ciudad o “Remote, CET overlap”; experiencia con verbos y métricas; skills en texto; una columna. Quita la foto. Pasa idiomas a fluidez US (“Professional working proficiency”) o deja CEFR si el equipo es mixto. No traduzcas “grado” a “grade”; usa BSc/BA si aplica.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'La foto no viaja a un resume US',
          lead: 'Si aplicas a un ATS americano con un CV español de dos columnas y retrato, el filtro y el sesgo trabajan contra ti. Sube el archivo y deja texto A4 limpio; luego quita foto y datos extra.',
          from: 'CV ES con foto',
          to: 'Resume sin datos personales de más',
          button: 'Adaptar el formato',
        },
        { type: 'h2', text: 'Europa hacia España (el caso inverso)' },
        {
          type: 'p',
          text: 'Un resume US de una página en un proceso local español puede parecer incompleto (sin foto donde aún se espera, sin MCER, demasiado telegrama). Añade idiomas A1–C2, considera foto si el mercado es local, A4, y no dejes el teléfono sin prefijo. El contenido de logros se queda. En ClarCV regeneras plantilla; tú decides foto y Letter vs A4 según el destino. El modelo no debe inventar una green card ni un NIE.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Resume y CV son lo mismo?',
              a: 'En US, resume = empleo (corto). CV = académico (largo). En España, “CV” es el documento de empleo. No mezcles las longitudes.',
            },
            {
              q: '¿Puedo mandar A4 a un portal US?',
              a: 'Muchos lo aceptan. Letter encaja mejor al imprimir. Lo crítico es una columna y sin foto.',
            },
            {
              q: '¿Pongo “Authorized to work in the EU”?',
              a: 'Si el proceso es US/UK y es verdad, una línea ayuda. No pongas el número de NIE en el PDF.',
            },
            {
              q: '¿ClarCV convierte a Letter automáticamente?',
              a: 'El producto está pensado en A4 ATS. Para US, recorta campos y, si exigen Letter, exporta desde un editor a ese tamaño.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'US vs European CV: résumé, photo, and A4',
      description:
        'One-page résumé, no photo or age, Letter; European A4 CV, sometimes a photo. How to adapt the file you already have. Not just language. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'US vs European CV is not a synonym of English vs Spanish. Document language follows the ad: [CV in English or Spanish](/blog/cv-ingles-o-espanol). Here the file’s continent changes: a US résumé of one page, Letter, no photo, no age, no marital status; a European A4 CV of one or two pages, sometimes with a photo, languages as CEFR. Sending a New York résumé to InfoJobs (or a CV with photo and ID to Greenhouse in Austin) is a format error, not a translation error.',
        },
        {
          type: 'p',
          text: 'Photo: [photo on the CV](/blog/foto-en-el-cv). Extra fields: [what not to include](/blog/que-no-poner-en-el-cv). ClarCV exports A4 for ES/LatAm/EU. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not ship US Letter as the main product; if the process is fully American, drop photo and extra data and respect their page.',
        },
        {
          type: 'p',
          text: '“CV” in UK academia can be 3–10 pages with publications. That is not a product résumé in San Francisco. Name the document as they do.',
        },
        { type: 'h2', text: 'United States and Canada (private)' },
        {
          type: 'p',
          text: 'Résumé: 1 page junior/mid; 2 only with a dense path. No photo, no date of birth, no national ID, no gender, no nationality except work authorization (“US work authorized” / “TN eligible”). European-style personal statements, out. Short summary. Achievements with figures. Cover letter separate if they ask. Letter size (8.5 × 11 in). US ATS hates the same things: columns, icons, tables.',
        },
        {
          type: 'ul',
          items: [
            'UK private (non-academic): increasingly a 2-page “CV”, often no photo.',
            'Spain / much of the EU: A4, 1–2 pages, photo still common locally, not at US subsidiaries.',
            'Germany: sometimes a more rigid Lebenslauf and a photo; read the ad.',
            'LatAm: hoja de vida / CV — map in [hoja de vida or currículum](/blog/hoja-de-vida-o-curriculum).',
          ],
        },
        { type: 'h2', text: 'What to change on the PDF you already use in Spain' },
        {
          type: 'p',
          text: 'Before (Madrid → US remote process): photo, “age 32”, street address, hobbies, two Canva columns. After: name, email, phone +1 or +34, LinkedIn, city or “Remote, CET overlap”; experience with verbs and metrics; skills as text; one column. Remove the photo. Switch languages to US fluency labels (“Professional working proficiency”) or keep CEFR if the team is mixed. Do not translate “grado” as “grade”; use BSc/BA if it applies.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The photo does not travel to a US résumé',
          lead: 'If you apply to an American ATS with a Spanish two-column CV and a portrait, the filter and bias work against you. Upload the file and leave clean A4 text; then drop photo and extra fields.',
          from: 'Spanish CV with photo',
          to: 'Résumé without extra personal data',
          button: 'Adapt the format',
        },
        { type: 'h2', text: 'Europe into Spain (the reverse case)' },
        {
          type: 'p',
          text: 'A one-page US résumé in a local Spanish process can look incomplete (no photo where it is still expected, no CEFR, too much telegram). Add A1–C2 languages, consider a photo if the market is local, A4, and do not leave the phone without a prefix. Achievement content stays. In ClarCV you regenerate a template; you decide photo and Letter vs A4 by destination. The model must not invent a green card or an NIE.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Are résumé and CV the same?',
              a: 'In the US, résumé = jobs (short). CV = academic (long). In Spain, “CV” is the job document. Do not mix the lengths.',
            },
            {
              q: 'Can I send A4 to a US board?',
              a: 'Many accept it. Letter fits printing better. The critical bit is one column and no photo.',
            },
            {
              q: 'Should I put “Authorized to work in the EU”?',
              a: 'If the process is US/UK and it is true, one line helps. Do not put the NIE number on the PDF.',
            },
            {
              q: 'Does ClarCV convert to Letter automatically?',
              a: 'The product is built around A4 ATS. For the US, trim fields and, if they require Letter, export from an editor at that size.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'peso-cv-pdf',
    date: '2026-08-22',
    category: 'format',
    related: ['cv-pdf-vs-word', 'enviar-cv-por-email', 'cv-para-portales-empleo'],
    es: {
      title: 'Peso del CV en PDF: límites de email y portales',
      description:
        'Por qué el currículum pesa demasiado (la foto), qué hacen InfoJobs y el adjunto de Gmail, y cómo aligerar sin convertirlo en imagen. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'Un CV en PDF “muy pesado” casi nunca es el texto. Es la foto a 4000 px, un fondo de Canva, o un export a calidad máxima. El email rebota, InfoJobs o el ATS corporativo rechazan el adjunto, o el reclutador no espera a que baje 12 MB en el móvil. No hay un único techo universal: Gmail y Outlook suelen tragar más que un portal. La regla práctica: apunta a unos pocos cientos de KB, no a varios MB, salvo que el anuncio pida un book.',
        },
        {
          type: 'p',
          text: 'Formato de envío: [PDF vs Word](/blog/cv-pdf-vs-word). Canal: [email](/blog/enviar-cv-por-email) y [portales](/blog/cv-para-portales-empleo). ClarCV regenera un A4 ligero (plantilla, no un escaneo). Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No comprimimos tu portfolio de 40 MB; el CV de empleo no es ese archivo.',
        },
        {
          type: 'p',
          text: 'Si el PDF no deja seleccionar texto, ya no es un tema de peso: es un PDF-imagen. Arréglalo antes de comprimir.',
        },
        { type: 'h2', text: 'Qué suele inflar el archivo' },
        {
          type: 'p',
          text: 'Antes: retrato recortado de un RAW, banner a sangre, iconos PNG enormes, tres páginas. Después: foto 300–600 px de ancho si la pones, fondo blanco, iconos fuera (texto), 1–2 páginas. Exporta “PDF” desde el editor, no “imprimir a PDF” desde un JPEG de Illustrator. Evita incrustar el mismo logo cinco veces a máxima resolución.',
        },
        {
          type: 'ul',
          items: [
            'Foto: el culpable nº 1. Redimensiona antes de colocarla; no dejes el original de la cámara.',
            'Canva / Figma: aplanados y sombras suben el peso. Una plantilla de una columna pesa menos.',
            'Fuentes no incrustadas a veces duplican; usa familias de sistema (ver [color y fuente](/blog/color-y-fuente-cv)).',
            'Portafolio: enlace, no 15 capturas dentro del CV.',
          ],
        },
        { type: 'h2', text: 'Límites: mira el portal, no un mito de 2 MB' },
        {
          type: 'p',
          text: 'Cada tablero cambia el tope. InfoJobs, LinkedIn Easy Apply y los career sites de SAP/Workday muestran el error al subir (“archivo demasiado grande”). No copies un número de un foro de 2018. Si el email del reclutador no indica límite, un PDF de CV de &lt; 1 MB es cortés. Un WeTransfer para un currículum de una página es una señal de que el archivo está mal hecho, no de que tu carrera “no cabe”.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '12 MB no es un CV, es un álbum',
          lead: 'Si el portal rechaza el adjunto o el email rebota, la foto o Canva suelen ser la causa. Sube el PDF y regenera un A4 ligero con texto real.',
          from: 'PDF de 8–12 MB',
          to: 'CV de unos cientos de KB',
          button: 'Aligerar mi PDF',
        },
        { type: 'h2', text: 'Comprimir sin matar el parseo' },
        {
          type: 'p',
          text: 'No pases el CV por un “PDF compressor” que rasteriza cada página a imagen: el ATS deja de leer. Reexporta desde Word/Google Docs/ClarCV. Si usas Acrobat, reduce tamaño conservando texto. Comprueba después: seleccionar un párrafo y pegar en el bloc de notas. En ClarCV el output es plantilla A4, no un JPEG disfrazado. Tú no sustituyes un book de dirección de arte por este PDF.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Cuál es el peso máximo “oficial”?',
              a: 'El del portal o del servidor de correo en ese envío. Como hábito, mantén el CV de empleo muy por debajo de 1–2 MB.',
            },
            {
              q: '¿Quito la foto para pesar menos?',
              a: 'Si el mercado no la exige, sí, y ganas parseo. Si España local aún la espera, redimensiónala; no la dejes en 5 MB sola.',
            },
            {
              q: '¿DOCX pesa menos que PDF?',
              a: 'A veces. Si el anuncio pide PDF, aligera el PDF. No cambies la extensión a mano.',
            },
            {
              q: '¿ClarCV garantiza un tamaño concreto?',
              a: 'No un número mágico. Las plantillas evitan fondos pesados e imágenes enormes. Una foto que subas tú sigue siendo tuya de comprimir.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV PDF file size: email and job-board limits',
      description:
        'Why a résumé is too heavy (the photo), what InfoJobs and Gmail attachments do, and how to slim it without turning it into an image. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'A “too heavy” PDF CV is almost never the words. It is a 4000 px photo, a Canva background, or a maximum-quality export. Email bounces, InfoJobs or the corporate ATS rejects the attachment, or the recruiter will not wait for 12 MB on a phone. There is no single universal cap: Gmail and Outlook usually swallow more than a board. Practical rule: aim at a few hundred KB, not several MB, unless the ad asks for a book.',
        },
        {
          type: 'p',
          text: 'Send format: [PDF vs Word](/blog/cv-pdf-vs-word). Channel: [email](/blog/enviar-cv-por-email) and [job boards](/blog/cv-para-portales-empleo). ClarCV regenerates a light A4 (template, not a scan). Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not compress your 40 MB portfolio; the job CV is not that file.',
        },
        {
          type: 'p',
          text: 'If the PDF will not let you select text, it is no longer a size topic: it is an image PDF. Fix that before you compress.',
        },
        { type: 'h2', text: 'What usually inflates the file' },
        {
          type: 'p',
          text: 'Before: a cropped RAW portrait, a full-bleed banner, huge PNG icons, three pages. After: photo 300–600 px wide if you include one, white background, icons out (text), 1–2 pages. Export “PDF” from the editor, not “print to PDF” from an Illustrator JPEG. Avoid embedding the same logo five times at maximum resolution.',
        },
        {
          type: 'ul',
          items: [
            'Photo: culprit #1. Resize before placing; do not leave the camera original.',
            'Canva / Figma: flats and shadows raise weight. A one-column template weighs less.',
            'Unembedded fonts sometimes duplicate; use system families (see [color and type](/blog/color-y-fuente-cv)).',
            'Portfolio: a link, not 15 screenshots inside the CV.',
          ],
        },
        { type: 'h2', text: 'Limits: read the board, not a 2018 2 MB myth' },
        {
          type: 'p',
          text: 'Each board changes the cap. InfoJobs, LinkedIn Easy Apply, and SAP/Workday career sites show the error on upload (“file too large”). Do not copy a number from a 2018 forum. If the recruiter’s email states no limit, a CV PDF of &lt; 1 MB is polite. WeTransfer for a one-page résumé is a sign the file is badly made, not that your career “does not fit.”',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '12 MB is not a CV, it is an album',
          lead: 'If the board rejects the attachment or email bounces, the photo or Canva is usually why. Upload the PDF and regenerate a light A4 with real text.',
          from: '8–12 MB PDF',
          to: 'A CV of a few hundred KB',
          button: 'Slim my PDF',
        },
        { type: 'h2', text: 'Compress without killing parsing' },
        {
          type: 'p',
          text: 'Do not run the CV through a “PDF compressor” that rasters every page to an image: ATS stops reading. Re-export from Word/Google Docs/ClarCV. If you use Acrobat, reduce size while keeping text. Then check: select a paragraph and paste into Notepad. In ClarCV the output is an A4 template, not a JPEG in disguise. You do not replace an art-direction book with this PDF.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'What is the “official” maximum size?',
              a: 'Whatever that portal or mail server allows on that send. As a habit, keep the job CV well under 1–2 MB.',
            },
            {
              q: 'Should I drop the photo to weigh less?',
              a: 'If the market does not require it, yes, and you gain parsing. If local Spain still expects it, resize it; do not leave it as 5 MB alone.',
            },
            {
              q: 'Does DOCX weigh less than PDF?',
              a: 'Sometimes. If the ad wants PDF, slim the PDF. Do not rename the extension by hand.',
            },
            {
              q: 'Does ClarCV guarantee a specific size?',
              a: 'Not a magic number. Templates avoid heavy backgrounds and huge images. A photo you upload is still yours to compress.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-teletrabajo',
    date: '2026-08-22',
    category: 'career',
    related: ['adaptar-cv-a-una-oferta', 'linkedin-y-cv', 'cv-ingles-o-espanol'],
    es: {
      title: 'CV para teletrabajo: ciudad, zona horaria y remoto',
      description:
        'Cómo dejar claro en el currículum que buscas remoto o híbrido: ciudad, CET, derecho a trabajar. Keywords que el ATS sí lee. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Un CV para teletrabajo no es un PDF distinto: es el mismo archivo con ciudad, zona horaria y la palabra que usa el anuncio (remoto, teletrabajo, remote, hybrid). Ocultar que vives en Badajoz cuando el puesto es “Madrid híbrido 3 días” no te hace más contratable; te quema en la primera call. Poner solo “Remote” sin país cuando piden overlap con CET tampoco. Si ya tienes un CV, cambia la cabecera y el resumen, no la biografía.',
        },
        {
          type: 'p',
          text: 'Alinea keywords a la oferta: [adaptar el CV](/blog/adaptar-cv-a-una-oferta). El perfil público: [LinkedIn y CV](/blog/linkedin-y-cv). Idioma del PDF: [inglés o español](/blog/cv-ingles-o-espanol). ClarCV deja “remoto” en texto, no en un icono de casita. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        {
          type: 'p',
          text: '“Tengo Starlink y una silla ergonómica” no es una skill. Derecho a trabajar en la UE o visado, sí puede ser una línea.',
        },
        { type: 'h2', text: 'Cabecera: dónde estás y qué aceptas' },
        {
          type: 'p',
          text: 'Antes: sin ciudad, o “Open to the world”, o un pin de mapa. Después: Granada · Remoto (CET) · Híbrido Madrid 1 semana/mes si es verdad. O: “Lisboa, overlap 14–18 CET.” El ATS busca remote, teletrabajo, híbrido, work from home. El humano busca si puede verte el martes. No mientas la residencia: nómina, IRPF y convenio lo preguntarán.',
        },
        {
          type: 'ul',
          items: [
            'Oferta 100 % remoto ES: “Remoto España” + comunidad o ciudad.',
            'Remoto UE: país y autorización (“autorizado a trabajar en la UE”).',
            'Remoto US para alguien en España: zona horaria y visado; el resume US sin foto — [americano vs europeo](/blog/cv-americano-vs-europeo).',
            'Híbrido: días o % si el anuncio los nombra. “Flexible” solo es débil.',
            'Disponibilidad para viajar: una línea, no un ensayo.',
          ],
        },
        { type: 'h2', text: 'Experiencia: prueba de remoto, no de Wi-Fi' },
        {
          type: 'p',
          text: 'Viñetas: herramientas (Slack, Meet, Jira), husos con los que ya trabajaste, entregas asíncronas. No listes “teletrabajo” como hobby. Si todos tus puestos fueron oficina, no inventes un 100 % remote: di “colaboración con equipo en Dublín (calls semanales)”. El resumen: “CSM B2B, remoto desde 2021, inglés C1.” Tres anclas. Relación con [disponibilidad](/blog/disponibilidad-inmediata-cv) si puedes empezar ya en remoto.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'La casita no es la keyword “remoto”',
          lead: 'Si tu modalidad es un pictograma, el ATS no extrae teletrabajo ni remote. Sube el PDF y deja ciudad + remoto en texto.',
          from: 'Icono de home office',
          to: 'Remoto · CET en la cabecera',
          button: 'Dejar claro el teletrabajo',
        },
        { type: 'h2', text: 'LinkedIn y el PDF, la misma modalidad' },
        {
          type: 'p',
          text: 'Si LinkedIn dice “On-site Madrid” y el CV “Full remote worldwide”, el reclutador desconfía. Alinea. Open to work: elige Remote / Hybrid / On-site como en el archivo. En ClarCV no tocamos LinkedIn; tú pegas la URL en texto ([URL de LinkedIn](/blog/linkedin-url-en-el-cv)). El modelo no debe inventar que vives en Ámsterdam.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Quito la ciudad para parecer más remoto?',
              a: 'No. La ciudad o el país aclaran huso, convenio y visitas. “Solo Remote” sin geografía retrasa.',
            },
            {
              q: '¿Pongo el setup (monitor, silla)?',
              a: 'No en el CV. En una entrevista de IT, si preguntan. El PDF no es un inventario.',
            },
            {
              q: '¿“Digital nomad” en la cabecera?',
              a: 'Solo si el anuncio lo celebra. Muchas empresas quieren un país fiscal estable. Prefiere ciudad + CET.',
            },
            {
              q: '¿ClarCV detecta si una oferta es remota?',
              a: 'No. Tú copias la modalidad del anuncio al PDF. Nosotros lo dejamos parseable.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV for remote work: city, time zone, and WFH',
      description:
        'How to make a résumé show remote or hybrid: city, CET, right to work. Keywords ATS actually reads. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A CV for remote work is not a different PDF: it is the same file with city, time zone, and the word the ad uses (remote, teletrabajo, hybrid). Hiding that you live in Badajoz when the job is “Madrid hybrid 3 days” does not make you more hirable; it burns you on the first call. Putting only “Remote” with no country when they need CET overlap does not help either. If you already have a CV, change the header and summary, not the biography.',
        },
        {
          type: 'p',
          text: 'Align keywords to the ad: [tailor the CV](/blog/adaptar-cv-a-una-oferta). Public profile: [LinkedIn and CV](/blog/linkedin-y-cv). PDF language: [English or Spanish](/blog/cv-ingles-o-espanol). ClarCV leaves “remote” as text, not a little-house icon. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        {
          type: 'p',
          text: '“I have Starlink and an ergonomic chair” is not a skill. Right to work in the EU or a visa can be one line.',
        },
        { type: 'h2', text: 'Header: where you are and what you accept' },
        {
          type: 'p',
          text: 'Before: no city, or “Open to the world”, or a map pin. After: Granada · Remote (CET) · Hybrid Madrid 1 week/month if true. Or: “Lisbon, overlap 14–18 CET.” ATS looks for remote, teletrabajo, hybrid, work from home. The human looks at whether they can see you on Tuesday. Do not lie about residence: payroll, tax, and the contract will ask.',
        },
        {
          type: 'ul',
          items: [
            'Fully remote Spain ad: “Remote Spain” + region or city.',
            'Remote EU: country and authorization (“authorized to work in the EU”).',
            'US remote for someone in Spain: time zone and visa; US résumé with no photo — [US vs European](/blog/cv-americano-vs-europeo).',
            'Hybrid: days or % if the ad names them. “Flexible” alone is weak.',
            'Willingness to travel: one line, not an essay.',
          ],
        },
        { type: 'h2', text: 'Experience: proof of remote, not of Wi-Fi' },
        {
          type: 'p',
          text: 'Bullets: tools (Slack, Meet, Jira), time zones you already worked with, async delivery. Do not list “remote work” as a hobby. If every job was on-site, do not invent 100% remote: say “collaboration with a Dublin team (weekly calls).” Summary: “B2B CSM, remote since 2021, English C1.” Three anchors. Tie to [availability](/blog/disponibilidad-inmediata-cv) if you can start remote now.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The little house is not the keyword “remote”',
          lead: 'If work mode is a pictogram, ATS will not extract teletrabajo or remote. Upload the PDF and leave city + remote as text.',
          from: 'Home-office icon',
          to: 'Remote · CET in the header',
          button: 'Make remote work obvious',
        },
        { type: 'h2', text: 'LinkedIn and the PDF, the same work mode' },
        {
          type: 'p',
          text: 'If LinkedIn says “On-site Madrid” and the CV says “Full remote worldwide,” the recruiter distrusts you. Align. Open to work: pick Remote / Hybrid / On-site as on the file. ClarCV does not touch LinkedIn; you paste the URL as text ([LinkedIn URL](/blog/linkedin-url-en-el-cv)). The model must not invent that you live in Amsterdam.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I drop the city to look more remote?',
              a: 'No. City or country clarify time zone, contract, and visits. “Remote only” with no geography delays you.',
            },
            {
              q: 'Should I list the setup (monitor, chair)?',
              a: 'Not on the CV. In an IT interview if they ask. The PDF is not an inventory.',
            },
            {
              q: '“Digital nomad” in the header?',
              a: 'Only if the ad celebrates it. Many companies want a stable tax country. Prefer city + CET.',
            },
            {
              q: 'Does ClarCV detect if a job is remote?',
              a: 'No. You copy the ad’s work mode onto the PDF. We leave it parseable.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
